import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Building2, Mail, Lock, User, Phone, ArrowLeft, Shield, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';

export default function RegisterLandlordPage() {
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [validatingInvite, setValidatingInvite] = useState(!!invitationToken);
  const [invitationValid, setInvitationValid] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [registered, setRegistered] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (invitationToken) {
      validateInvitation();
    }
  }, [invitationToken]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  const validateInvitation = async () => {
    try {
      setValidatingInvite(true);
      const response = await authAPI.getLandlordInvitation(invitationToken);
      setInvitationData(response.data);
      setInvitationValid(true);
      // Pre-fill form with invitation data
      setFormData(prev => ({
        ...prev,
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired invitation');
      setInvitationValid(false);
    } finally {
      setValidatingInvite(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    try {
      setLoading(true);
      const response = await authAPI.register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: 'landlord',
        phone: formData.phone || null,
        invitation_token: invitationToken || null,
      });

      // If invited, user is pre-verified - log them in directly
      if (invitationValid && invitationToken) {
        login(response.data.user, response.data.token);
        toast.success('Welcome! Your account is ready to use.');
        navigate('/landlord');
      } else {
        // Show pending verification screen for non-invited
        setRegistered(true);
        toast.success('Registration successful! Please wait for admin verification.');
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Loading state for invitation validation
  if (validatingInvite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F0E8] to-[#E8E0D5] dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#C85A42] mb-4" />
            <p className="text-muted-foreground">Validating invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid invitation state
  if (invitationToken && !invitationValid && !validatingInvite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F0E8] to-[#E8E0D5] dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <Clock className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invalid or Expired Invitation</h2>
            <p className="text-muted-foreground mb-6">
              This invitation link is no longer valid. Please contact the administrator for a new invitation.
            </p>
            <Link to="/register">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Registration
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state after registration
  if (registered) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F0E8] to-[#E8E0D5] dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
              <Clock className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Registration Submitted!</h2>
            <p className="text-muted-foreground mb-6">
              Your landlord account is pending verification. Our admin team will review your application and you'll receive a notification once approved.
            </p>
            <Link to="/login">
              <Button className="bg-[#2F4F4F] hover:bg-[#1a3a3a]">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F0E8] to-[#E8E0D5] dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/register" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Link>
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-[#2F4F4F]">
              <Building2 className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">
            {invitationValid ? 'Complete Your Registration' : 'Landlord Registration'}
          </CardTitle>
          <CardDescription>
            {invitationValid ? (
              <span className="flex items-center justify-center gap-2 text-green-600">
                <CheckCircle className="w-4 h-4" />
                You've been invited! Your account will be pre-verified.
              </span>
            ) : (
              'Create your property owner account'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Info Banner */}
          {!invitationValid && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Admin Verification Required</p>
                  <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                    For security, landlord accounts require admin approval before activation. You'll be notified once verified.
                  </p>
                </div>
              </div>
            </div>
          )}

          {invitationValid && invitationData?.subscription_tier && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    Starting with {invitationData.subscription_tier.charAt(0).toUpperCase() + invitationData.subscription_tier.slice(1)} Plan
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-300 mt-1">
                    Your subscription will be activated immediately after registration.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  name="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  disabled={invitationValid}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  disabled={invitationValid}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number (Optional)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+63 9XX XXX XXXX"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="terms-agreement"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                data-testid="terms-agreement-checkbox"
              />
              <label htmlFor="terms-agreement" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                I agree to the <Link to="/terms-of-service" target="_blank" className="text-[#C85A42] hover:underline font-medium">Terms of Service</Link> and <Link to="/privacy-policy" target="_blank" className="text-[#C85A42] hover:underline font-medium">Privacy Policy</Link>
              </label>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#2F4F4F] hover:bg-[#1a3a3a]"
              disabled={loading || !agreedToTerms}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : invitationValid ? (
                'Complete Registration'
              ) : (
                'Submit for Verification'
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-[#C85A42] hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
